/**
 * Los cuestionarios de una asignatura.
 *
 * Dos cosas cambian de raíz respecto a lo que había. Una, que la lista se resolvía con 1+2N
 * peticiones en serie —con veinte cuestionarios, cuarenta y una— y no se pintaba una sola fila
 * hasta que volvía la última; ahora son tres consultas, pasen los que pasen. Y dos, que se
 * calculaba qué le falta a cada cuestionario y **se tiraba el resultado**: quedaba un «Todavía no
 * está listo» sin decir por qué, con la única acción escondida y sin forma de saber qué arreglar.
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import {
  assessQuiz,
  type ApiErrorKey,
  type QuizReadiness,
  type RawQuestion,
  type SessionProfile,
} from '@clica/shared';
import {
  createQuiz,
  deleteQuiz,
  getSubject,
  listOptions,
  listQuestionsOf,
  listQuizzes,
  type Quiz,
  type Subject,
} from '../content/api.js';
import { ApiRequestError, createGame } from '../api/client.js';
import { useAvisos } from '../components/sistema/avisos.js';
import { Boton } from '../components/sistema/boton.js';
import { DialogoConfirmacion } from '../components/sistema/dialogo-confirmacion.js';
import { Glifo } from '../components/sistema/glifo.js';
import { Marco } from '../components/sistema/marco.js';
import { MarcaAsignatura } from '../components/sistema/marca-asignatura.js';
import { Rotulo } from '../components/sistema/rotulo.js';
import { useRecurso, VistaRecurso } from '../components/sistema/recurso.js';

/** Lo que hace falta saber de un cuestionario para pintarlo en la lista. */
interface FilaCuestionario {
  readonly quiz: Quiz;
  readonly estado: QuizReadiness;
}

interface Contenido {
  readonly subject: Subject | null;
  readonly filas: readonly FilaCuestionario[];
}

/**
 * Carga la asignatura y sus cuestionarios, y decide de cada uno si se puede jugar.
 *
 * Tres consultas: los cuestionarios, todas sus preguntas de una vez y todas sus opciones de una
 * vez. La comprobación es la misma función pura que usa la importación: aquí no se cuenta nada a
 * mano.
 */
async function cargar(subjectId: string): Promise<Contenido> {
  const [subject, quizzes] = await Promise.all([getSubject(subjectId), listQuizzes(subjectId)]);

  const preguntas = await listQuestionsOf(quizzes.map((q) => q.id));
  const opciones = await listOptions(preguntas.map((q) => q.id));

  const filas = quizzes.map((quiz) => {
    const suyas: RawQuestion[] = preguntas
      .filter((pregunta) => pregunta.quiz_id === quiz.id)
      .map((pregunta) => ({
        id: pregunta.id,
        position: pregunta.position,
        type: pregunta.type,
        prompt_md: pregunta.prompt_md,
        options: opciones
          .filter((opcion) => opcion.question_id === pregunta.id)
          .map((opcion) => ({
            position: opcion.position,
            text_md: opcion.text_md,
            is_correct: opcion.is_correct,
          })),
      }));

    return { quiz, estado: assessQuiz(suyas) };
  });

  return { subject, filas };
}

/**
 * Qué le falta a un cuestionario para poder jugarse.
 *
 * Los mensajes por problema ya estaban traducidos en los tres idiomas y no los usaba nadie: se
 * enseñaba «Todavía no está listo» y ahí se acababa la ayuda.
 */
function QueFalta({
  estado,
  quizId,
}: {
  estado: QuizReadiness;
  quizId: string;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  if (estado.playable) return null;

  if (estado.reason === 'noQuestions') {
    return (
      <p className="text-body text-text-dim mt-1">
        {t('quizzes.needsQuestions')}{' '}
        <Link to={`/cuestionarios/${quizId}`} className="underline underline-offset-4">
          {t('quizzes.openEditor')}
        </Link>
      </p>
    );
  }

  const problemas = (estado.issues ?? []).slice(0, 3);
  return (
    <ul className="text-body text-text-dim mt-1 flex flex-col gap-0.5">
      {problemas.map((issue) => (
        <li key={issue.questionId}>
          <Link
            to={`/cuestionarios/${quizId}#pregunta-${issue.position + 1}`}
            className="underline underline-offset-4"
          >
            {t('validation.goToQuestion', { number: issue.position + 1 })}
          </Link>{' '}
          — {issue.problems.map((problema) => t(`validation.${problema.reason}`)).join('; ')}
        </li>
      ))}
      {(estado.issues ?? []).length > problemas.length && (
        <li>{t('quizzes.andMore', { count: (estado.issues ?? []).length - problemas.length })}</li>
      )}
    </ul>
  );
}

function Fila({
  fila,
  onChanged,
  retraso,
}: {
  fila: FilaCuestionario;
  onChanged: () => void;
  retraso: number;
}): React.JSX.Element {
  const { t } = useTranslation();
  const avisos = useAvisos();
  const [porBorrar, setPorBorrar] = useState(false);
  const { quiz, estado } = fila;

  async function borrar(): Promise<void> {
    setPorBorrar(false);
    try {
      await deleteQuiz(quiz.id);
      avisos.correcto(t('quizzes.deleted', { title: quiz.title }));
      onChanged();
    } catch (error) {
      console.error('No se ha podido borrar el cuestionario', error);
      avisos.fallo(t('common.saveFailed'));
    }
  }

  return (
    <li
      className="entra rounded-card border-line bg-surface flex flex-wrap items-start gap-4 border-2 p-4"
      style={{ '--retraso': `${retraso}ms` } as React.CSSProperties}
    >
      <div className="min-w-0 flex-1">
        <Link
          to={`/cuestionarios/${quiz.id}`}
          className="font-display text-h3 font-bold underline-offset-4 hover:underline"
        >
          {quiz.title}
        </Link>

        {/* Estado por glifo y palabra, nunca solo por color (principio IV). */}
        <p
          className={[
            'text-body mt-1 flex items-center gap-1.5 font-bold',
            estado.playable ? 'text-ok' : 'text-text-dim',
          ].join(' ')}
        >
          <Glifo nombre={estado.playable ? 'correcto' : 'vacio'} size={18} />
          {estado.playable ? t('quizzes.playable') : t('quizzes.notPlayable')}
        </p>

        <QueFalta estado={estado} quizId={quiz.id} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {/* Solo se ofrece si está listo: el servidor lo rechazaría igual, y ofrecer un botón que
            siempre falla es peor que no ofrecerlo. Lo que ha cambiado es que ahora, cuando no
            está, se dice exactamente qué le falta. */}
        {estado.playable ? <BotonCrearSala quizId={quiz.id} /> : null}
        <Boton variante="destructiva" onClick={() => setPorBorrar(true)}>
          {t('quizzes.delete')}
        </Boton>
      </div>

      <DialogoConfirmacion
        abierto={porBorrar}
        destructivo
        titulo={t('quizzes.delete')}
        cuerpo={t('quizzes.deleteConfirm', { title: quiz.title })}
        confirmar={t('quizzes.delete')}
        onConfirmar={() => void borrar()}
        onCancelar={() => setPorBorrar(false)}
      />
    </li>
  );
}

export function PantallaCuestionarios({ profile }: { profile: SessionProfile }): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const avisos = useAvisos();
  const { subjectId = '' } = useParams();
  const [title, setTitle] = useState('');
  const [creando, setCreando] = useState(false);

  const recurso = useRecurso<Contenido>(
    useCallback(() => cargar(subjectId), [subjectId]),
    [subjectId],
  );

  const asignatura = recurso.valor.estado === 'listo' ? recurso.valor.datos.subject : null;
  const nombreAsignatura = asignatura?.name ?? '';

  useEffect(() => {
    const titulo = nombreAsignatura === '' ? t('quizzes.title') : nombreAsignatura;
    document.title = `${titulo} · ${t('app.name')}`;
  }, [nombreAsignatura, t]);

  /** Crear un cuestionario abre su editor: es lo que quiere hacer quien acaba de crearlo. */
  async function crear(evento: React.FormEvent): Promise<void> {
    evento.preventDefault();
    if (title.trim() === '') return;

    setCreando(true);
    try {
      const quiz = await createQuiz(profile.id, subjectId, title.trim());
      setTitle('');
      void navigate(`/cuestionarios/${quiz.id}`);
    } catch (error) {
      console.error('No se ha podido crear el cuestionario', error);
      avisos.fallo(t('common.saveFailed'));
    } finally {
      setCreando(false);
    }
  }

  return (
    <Marco
      profile={profile}
      migas={[
        { texto: t('subjects.title'), a: '/asignaturas' },
        { texto: nombreAsignatura === '' ? t('quizzes.title') : nombreAsignatura },
      ]}
    >
      <main id="contenido" className="mx-auto w-full max-w-3xl p-6">
        {/* El color de la asignatura existía y solo llegaba a su tarjeta: aquí es lo que dice
            de un vistazo en qué carpeta se está. */}
        <div className="flex items-center gap-4">
          {asignatura !== null && <MarcaAsignatura subject={asignatura} size={56} />}
          <div>
            <h1 className="font-display text-h1 font-extrabold text-balance">
              {nombreAsignatura === '' ? t('quizzes.title') : nombreAsignatura}
            </h1>
            <Rotulo className="block">{t('quizzes.title')}</Rotulo>
          </div>
        </div>

        <form onSubmit={(e) => void crear(e)} className="mt-6 flex flex-wrap items-end gap-3">
          <div className="min-w-48 flex-1">
            <Rotulo htmlFor="titulo-cuestionario" className="mb-1 block">
              {t('quizzes.titleField')}
            </Rotulo>
            <input
              id="titulo-cuestionario"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              className="text-body rounded-chip border-line bg-surface w-full border-2 px-3 py-2.5"
            />
          </div>
          <Boton type="submit" variante="primaria" cargando={creando}>
            {t('quizzes.create')}
          </Boton>
          <Link
            to={`/asignaturas/${subjectId}/importar`}
            className="text-body font-display rounded-chip border-line inline-flex min-h-11 items-center border-2 px-5 font-bold"
          >
            {t('import.title')}
          </Link>
        </form>

        <VistaRecurso
          recurso={recurso}
          // El vacío nunca salta aquí porque los datos son un objeto, así que se resuelve dentro.
        >
          {(datos) =>
            datos.filas.length === 0 ? (
              <div className="rounded-card border-line mt-6 border-2 border-dashed p-8 text-center">
                <p className="font-display text-h3 font-bold">{t('quizzes.empty')}</p>
                <p className="text-body text-text-dim mt-2">{t('quizzes.emptyLead')}</p>
              </div>
            ) : (
              <ul className="mt-6 flex flex-col gap-3">
                {datos.filas.map((fila, indice) => (
                  <Fila
                    key={fila.quiz.id}
                    fila={fila}
                    onChanged={recurso.recargar}
                    retraso={Math.min(indice, 6) * 45}
                  />
                ))}
              </ul>
            )
          }
        </VistaRecurso>
      </main>
    </Marco>
  );
}

/**
 * Traduce la clave del servidor a la de la interfaz.
 *
 * Son dos vocabularios distintos a propósito: el del protocolo es estable y el de la interfaz
 * cambia con el texto. Un mapa explícito deja de compilar si aparece una clave nueva sin mensaje.
 */
function mensajeDeError(key: ApiErrorKey | 'auth.error.generic') {
  switch (key) {
    case 'game.error.quiz_not_found':
      return 'game.error.quiz_not_found' as const;
    case 'game.error.quiz_not_playable':
      return 'game.error.quiz_not_playable' as const;
    case 'game.error.no_pin_available':
      return 'game.error.no_pin_available' as const;
    case 'auth.error.forbidden':
      return 'auth.error.forbidden' as const;
    default:
      return 'auth.error.generic' as const;
  }
}

/**
 * Abre la sala y lleva al proyector.
 *
 * La navegación ocurre **después** de que el servidor confirme el código: llevar antes al
 * proyector mostraría cuatro cifras que todavía no existen, y cuarenta alumnos las teclearían.
 */
function BotonCrearSala({ quizId }: { quizId: string }): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const avisos = useAvisos();
  const [ocupado, setOcupado] = useState(false);

  return (
    // El fallo al abrir la sala es un SUCESO: pasa, se cuenta y se va. Se pintaba bajo el botón y
    // solo se limpiaba al reintentar, así que se quedaba en la lista de cuestionarios el resto de
    // la sesión, empujando las tarjetas de abajo.
    <span className="flex flex-col items-end gap-1">
      <Boton
        variante="primaria"
        cargando={ocupado}
        onClick={() => {
          setOcupado(true);
          createGame(quizId)
            .then((game) => navigate(`/host/${game.pin}`))
            .catch((cause: unknown) => {
              avisos.fallo(
                t(
                  mensajeDeError(
                    cause instanceof ApiRequestError ? cause.key : 'auth.error.generic',
                  ),
                ),
              );
              setOcupado(false);
            });
        }}
      >
        {ocupado ? t('game.host.creating') : t('game.host.create')}
      </Boton>
    </span>
  );
}
